// Helpers for internal message file attachments (images, PDFs, office and text
// files, and videos). keyon returns each attachment as a uid-only item
// { name, file_uid, kind, scan_status, patient_focus_id? } (a video also carries
// byte_size); the browser never sees a storage URL and reaches the bytes only
// through keyon's file.php.
import {
  DOCUMENT_REMOVED_TEXT,
  DOCUMENT_UNAVAILABLE_TEXT,
  SCAN_PENDING_TEXT,
  VIDEO_UNPLAYABLE_TEXT,
  fileAvailability,
  isFileUid,
  keyonFileUrl,
} from "@/utils/fileUrl";

export const MESSAGE_ATTACHMENT_ACCEPT =
  "image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv,application/pdf";

export const MAX_MESSAGE_ATTACHMENTS = 5;

// Same as keyon/php.ini upload_max_filesize (and /pdfs server limit).
export const MAX_MESSAGE_ATTACHMENT_BYTES = 20 * 1024 * 1024;
export const MAX_MESSAGE_ATTACHMENT_MB = Math.round(
  MAX_MESSAGE_ATTACHMENT_BYTES / (1024 * 1024)
);

// keyon/php.ini post_max_size is 24M: a message request whose files add up to
// more is cut off by PHP before keyon can answer with a sentence.
export const MAX_MESSAGE_REQUEST_BYTES = 24 * 1024 * 1024;

// keyon's image class: bmp, svg and heic/heif are refused server-side.
const IMAGE_EXT = /\.(jpe?g|png|gif|webp)$/i;
const PDF_EXT = /\.pdf$/i;
// keyon's video class takes ISO BMFF only (VideoContainer): not webm, mkv or avi.
const VIDEO_EXT = /\.(mp4|m4v|mov)$/i;
const OTHER_EXT = /\.(doc|docx|xls|xlsx|txt|csv)$/i;
const REFUSED_EXT = /\.(bmp|svg|heic|heif)$/i;
const REFUSED_TYPES = ["image/svg+xml", "image/bmp", "image/heic", "image/heif"];
const IMAGE_TYPES = ["image/jpeg", "image/png", "image/gif", "image/webp"];
const OTHER_TYPES = [
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "text/plain",
  "text/csv",
];
const KINDS = ["image", "pdf", "video", "other"];
// The containers keyon's video class reads, by the types a browser reports for them.
const VIDEO_TYPES = ["video/mp4", "video/quicktime", "video/x-m4v"];
// Anything that is a video to a person, whether or not keyon takes its container,
// so a picker can say "not this one" in words instead of "not a file I know".
const VIDEO_LIKE_EXT = /\.(mp4|m4v|mov|avi|wmv|mkv|flv|3gp|3g2|webm|mpe?g|ogv|m2ts|mts|asf|vob|f4v|divx)$/i;

/**
 * Whether this environment takes a video from a message: GET
 * video_upload.php?action=config says enabled and says a message may reference
 * one (refs.message, which an older keyon does not send). Anything else - no
 * answer, a failed call, a policy that lacks either - is no video, and the
 * picker and its checks are exactly what they were before video existed.
 * @param {object|null|undefined} policy the config answer
 */
export function videoOffered(policy) {
  return (
    !!policy &&
    policy.enabled === true &&
    !!policy.refs &&
    policy.refs.message === true &&
    (!Array.isArray(policy.domains) || policy.domains.includes("message"))
  );
}

/** A name that ends like a video, whatever the container. */
export function isVideoLikeName(name) {
  return VIDEO_LIKE_EXT.test(String(name || ""));
}

/** A picked file that is a video to a person: video/* or a video extension, accepted container or not. */
export function isVideoLikeFile(file) {
  if (!file) return false;
  return String(file.type || "").toLowerCase().startsWith("video/") || isVideoLikeName(file.name);
}

/** A picked file in one of the containers keyon reads (mp4, m4v, mov), by its type or its extension. */
export function isVideoFile(file) {
  if (!file) return false;
  return VIDEO_TYPES.includes(String(file.type || "").toLowerCase()) || VIDEO_EXT.test(String(file.name || ""));
}

/**
 * The picker's accept string. Today's when video is not offered; with video,
 * the server's own list of types and extensions appended, never video/* (a
 * webm or an avi would then be offered that keyon refuses).
 * @param {object|null|undefined} [policy] the config answer
 */
export function messageAttachmentAccept(policy) {
  if (!videoOffered(policy)) return MESSAGE_ATTACHMENT_ACCEPT;
  const safe = (v) => typeof v === "string" && /^[A-Za-z0-9.+/_-]+$/.test(v);
  const types = (Array.isArray(policy.mime_types) ? policy.mime_types : []).filter(safe);
  const extensions = (Array.isArray(policy.extensions) ? policy.extensions : [])
    .filter(safe)
    .map((e) => `.${e.replace(/^\./, "")}`);
  return [MESSAGE_ATTACHMENT_ACCEPT, ...types, ...extensions].join(",");
}

/** Display name for an attachment item, message row, or raw name string. */
export function getAttachmentName(itemOrMessageOrName) {
  if (itemOrMessageOrName && typeof itemOrMessageOrName === "object") {
    if (itemOrMessageOrName.name) return itemOrMessageOrName.name;
    const items = normalizeMessageAttachments(itemOrMessageOrName);
    if (items.length) return items[0].name;
    return "attachment";
  }
  return itemOrMessageOrName || "attachment";
}

/** keyon file.php URL for an item's uid, or null when it has none. */
export function resolveAttachmentUrl(item, { download = false } = {}) {
  return keyonFileUrl(item && item.file_uid, { download });
}

/**
 * image | pdf | video | other (drives the preview UI). keyon's `kind` wins; the
 * name is only a fallback for an item that carries none (or an unknown one).
 */
export function getAttachmentKind(itemOrMessage) {
  let item = itemOrMessage;
  if (item && typeof item === "object" && !("name" in item) && !("kind" in item) && "thread_attachments" in item) {
    item = normalizeMessageAttachments(item)[0] || null;
  }
  if (!item || typeof item !== "object") return "other";
  if (KINDS.includes(item.kind)) return item.kind;
  const name = String(item.name || "");
  if (IMAGE_EXT.test(name)) return "image";
  if (PDF_EXT.test(name)) return "pdf";
  if (VIDEO_EXT.test(name)) return "video";
  return "other";
}

/**
 * Parse thread_attachments → [{ name, file_uid, kind, scan_status, patient_focus_id }].
 * A video also keeps keyon's byte_size when it is a whole number; no other kind
 * carries one, and anything else keyon might add is not passed on.
 */
export function normalizeMessageAttachments(message) {
  if (!message) return [];

  let raw = message.thread_attachments;
  if (typeof raw === "string" && raw.trim()) {
    try {
      raw = JSON.parse(raw);
    } catch {
      raw = null;
    }
  }

  if (!Array.isArray(raw) || !raw.length) return [];

  return raw
    .map((item) => {
      if (!item || typeof item !== "object") return null;
      const fileUid = isFileUid(item.file_uid) ? item.file_uid : null;
      // A legacy url is never read: an item with neither a name nor a uid is nothing to show.
      if (!item.name && !fileUid) return null;
      const kind = getAttachmentKind(item);
      const byteSize = kind === "video" && Number.isSafeInteger(item.byte_size) && item.byte_size >= 0
        ? item.byte_size
        : null;
      return {
        name: item.name || "attachment",
        file_uid: fileUid,
        kind,
        scan_status: item.scan_status || null,
        // Prefer per-attachment stamp; fall back to message-level patient focus.
        patient_focus_id: item.patient_focus_id || message.patient_focus_id || null,
        // keyon marks a file its sender removed; only a real true counts, and the
        // key is left off otherwise, as keyon leaves it off.
        ...(item.removed === true ? { removed: true } : {}),
        ...(byteSize !== null ? { byte_size: byteSize } : {}),
      };
    })
    .filter(Boolean)
    .slice(0, MAX_MESSAGE_ATTACHMENTS);
}

export function hasMessageAttachment(message) {
  return normalizeMessageAttachments(message).length > 0;
}

/** Allow attachment-only sends: empty text becomes "(Attachment)" for the DB row. */
export function messageBodyWithOptionalAttachment(text, files) {
  const body = String(text || "").trim();
  if (body) return body;
  const list = Array.isArray(files) ? files : files ? [files] : [];
  if (list.length) return "(Attachment)";
  return "";
}

/** Append picked File(s) onto FormData for PHP multi-upload. */
export function appendMessageAttachment(formData, files) {
  // Use files[] so PHP builds a multi-file $_FILES["files"] array.
  // Plain "files" (no brackets) only keeps the last uploaded file.
  // Always pass a real File/Blob — Vue proxies break multipart bodies.
  const list = Array.isArray(files) ? files : files ? [files] : [];
  list.slice(0, MAX_MESSAGE_ATTACHMENTS).forEach((file) => {
    if (!file) return;
    const raw =
      file && typeof file === "object"
        ? file.__v_raw || file
        : file;
    const name = (raw && raw.name) || "attachment";
    formData.append("files[]", raw, name);
  });
  return formData;
}

/** "1.5 MB" for a byte count (1024-based, like the MB limits above); "" for anything that is not one. */
export function formatAttachmentSize(bytes) {
  if (!Number.isSafeInteger(bytes) || bytes < 0) return "";
  const units = ["B", "KB", "MB", "GB"];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  // One decimal, and none when it would be ".0".
  const text = unit === 0 ? String(value) : String(Math.round(value * 10) / 10);
  return `${text} ${units[unit]}`;
}

/** Short label for inbox thread previews (e.g. "Image", "2 attachments"). */
export function attachmentPreviewLabel(message) {
  const items = normalizeMessageAttachments(message);
  if (!items.length) return "";
  if (items.length === 1) {
    const kind = getAttachmentKind(items[0]);
    if (kind === "image") return "Image";
    if (kind === "pdf") return "PDF";
    if (kind === "video") return "Video";
    return items[0].name;
  }
  return `${items.length} attachments`;
}

/**
 * True when MIME or extension is an allowed messaging attachment type. A video
 * is allowed only when `policy` says the server takes one (videoOffered).
 */
export function isAllowedAttachmentFile(file, policy) {
  if (!file) return false;
  const type = String(file.type || "").toLowerCase();
  const name = String(file.name || "").toLowerCase();
  if (REFUSED_EXT.test(name) || REFUSED_TYPES.includes(type)) return false;
  // Some browsers omit MIME for images — accept by extension too, but a
  // browser-reported image type outside keyon's four is not an image we take.
  if (IMAGE_TYPES.includes(type)) return true;
  if (type.startsWith("image/")) return false;
  if (IMAGE_EXT.test(name)) return true;
  if (type === "application/pdf" || PDF_EXT.test(name)) return true;
  if (OTHER_EXT.test(name) || OTHER_TYPES.includes(type)) return true;
  return videoOffered(policy) && isVideoFile(file);
}

/**
 * True when file is within the 20MB messaging upload cap. A video, when the
 * server takes one, is held to the server's own limit instead: it does not go
 * through the request that cap is for.
 */
export function isAttachmentWithinSizeLimit(file, policy) {
  if (!file || typeof file.size !== "number") return false;
  if (videoOffered(policy) && isVideoFile(file)) {
    const max = policy.max_bytes;
    return typeof max === "number" && max > 0 ? file.size <= max : file.size <= MAX_MESSAGE_ATTACHMENT_BYTES;
  }
  return file.size <= MAX_MESSAGE_ATTACHMENT_BYTES;
}

/**
 * True when the files together fit in one message request (24 MiB). A video is
 * not in that request - it went up in chunks before the send and is referenced
 * by uid - so its bytes are not counted.
 */
export function isWithinMessageRequestLimit(files) {
  const list = Array.isArray(files) ? files : [];
  const total = list.reduce(
    (sum, f) => sum + (f && typeof f.size === "number" && !isVideoFile(f) ? f.size : 0),
    0
  );
  return total <= MAX_MESSAGE_REQUEST_BYTES;
}

/**
 * The uids of videos a send named that its response does not show on the
 * message. An older keyon (a replica not yet replaced, an image rolled back)
 * ignores video_file_uids, saves the message and answers 200 "Inserted" without
 * thread_attachments; keyon that could not bind a file sends it back with no
 * uid. Either way the video is not on the message, and the page must not tell
 * the sender it is.
 * @param {object|*} response the send's response body
 * @param {string[]} uids what the send named
 */
export function unattachedUids(response, uids) {
  const wanted = Array.isArray(uids) ? uids.filter(Boolean) : [];
  if (!wanted.length) return [];
  const present = new Set(
    normalizeMessageAttachments(response && typeof response === "object" ? response : null)
      .map((item) => item.file_uid)
      .filter(Boolean)
  );
  return wanted.filter((uid) => !present.has(uid));
}

/**
 * What an attachment shows and what a click does.
 *
 * An `other` file (office or text) is never rendered: no <img>, no frame, no
 * PDF.js, and never window.open — only a download, which keyon also forces to
 * an attachment. A pending scan disables the item; malicious, error or a
 * missing uid render unavailable.
 *
 * A video plays only in a bubble, in the page (action `play`: a click-to-play
 * card that mounts a <video>); anywhere else it offers nothing (`none`), and it
 * never gets `open`, because the page never opens a video in a new window.
 *
 * @param {object|null} item one normalized attachment
 * @param {'compact'|'bubble'|'compose'} variant
 */
export function attachmentPresentation(item, variant) {
  const kind = getAttachmentKind(item);
  const availability = fileAvailability(item || {});
  const blocked = { kind, availability, showImage: false, showPdfThumb: false, action: "none" };
  if (item && item.removed === true) {
    return { ...blocked, availability: "unavailable", stateText: DOCUMENT_REMOVED_TEXT };
  }
  if (availability === "unavailable") return { ...blocked, stateText: DOCUMENT_UNAVAILABLE_TEXT };
  if (availability === "scan_pending") return { ...blocked, stateText: SCAN_PENDING_TEXT };
  if (kind === "other") return { ...blocked, action: "download", stateText: null };
  if (kind === "video") {
    return { ...blocked, action: variant === "bubble" ? "play" : "none", stateText: null };
  }
  const action = variant === "compact" ? "download" : "open";
  return {
    kind,
    availability,
    showImage: kind === "image",
    showPdfThumb: kind === "pdf",
    action,
    stateText: null,
  };
}

/**
 * A download that came back 409 or 410 means the verdict changed after the list
 * was rendered; the item switches to that state and stops offering a click.
 * Any other result (ok, rate limited, a lapsed MFA session) leaves it as it was.
 *
 * @param {object} presentation from attachmentPresentation
 * @param {string|null} serverState a downloadKeyonFile failure state
 */
export function withServerState(presentation, serverState) {
  const blocked = { ...presentation, showImage: false, showPdfThumb: false, action: "none" };
  if (serverState === "scan_pending") {
    return { ...blocked, availability: "scan_pending", stateText: SCAN_PENDING_TEXT };
  }
  if (serverState === "unavailable") {
    return { ...blocked, availability: "unavailable", stateText: DOCUMENT_UNAVAILABLE_TEXT };
  }
  return presentation;
}

/**
 * A video keyon served that the browser would not decode (HEVC on a desktop
 * browser, a codec the platform lacks): the player is replaced by a download.
 * A verdict that already forbids the file (scanning, unavailable, removed)
 * stays as it is; a playback failure never turns it into an offer.
 *
 * @param {object} presentation from attachmentPresentation
 */
export function withPlaybackFailure(presentation) {
  if (!presentation || presentation.availability !== "available") return presentation;
  return {
    ...presentation,
    showImage: false,
    showPdfThumb: false,
    action: "download",
    stateText: VIDEO_UNPLAYABLE_TEXT,
  };
}

/**
 * After DeleteMessageAttachment: every message whose thread_attachments names
 * the file gets that item replaced by keyon's removed shape, in the column's
 * original form (JSON text stays text, a list stays a list), so the widget shows
 * the removal on every message that carried the file without a refetch. Other
 * messages come back as the same objects; nothing is changed in place.
 *
 * @param {object[]} messages
 * @param {string} fileUid
 * @returns {object[]}
 */
export function markAttachmentRemoved(messages, fileUid) {
  if (!Array.isArray(messages)) return [];
  if (!isFileUid(fileUid)) return messages.slice();
  return messages.map((message) => {
    if (!message || typeof message !== "object") return message;
    const raw = message.thread_attachments;
    const wasString = typeof raw === "string";
    let list = raw;
    if (wasString) {
      try {
        list = JSON.parse(raw);
      } catch {
        return message;
      }
    }
    if (!Array.isArray(list)) return message;
    let changed = false;
    const next = list.map((item) => {
      if (!item || typeof item !== "object" || item.file_uid !== fileUid) return item;
      changed = true;
      const removed = { name: item.name || "", file_uid: null, kind: null, scan_status: null, removed: true };
      if (item.patient_focus_id) removed.patient_focus_id = item.patient_focus_id;
      return removed;
    });
    if (!changed) return message;
    return { ...message, thread_attachments: wasString ? JSON.stringify(next) : next };
  });
}
